/**
 * RuleBuilder Component
 * 
 * Third step in the AlertForm wizard that allows users to configure conditions and values
 * for the alert rules they've selected in the previous step.
 */

import React, { useState, useEffect } from 'react';
import { useAlertContext } from '../../../contexts/AlertContext';
import { 
  AlertCondition, 
  getConditionDisplayText,
  AlertType
} from '../../../models/AlertModels';
import QWebToggle from '../../common/QWebToggle';
import '../../common/QWebToggleStyleLoader';

/**
 * RuleBuilder component for configuring alert rules
 * 
 * @returns {JSX.Element} The RuleBuilder component
 */
function RuleBuilder() {
  const { formState, updateFormState } = useAlertContext();
  const [validationErrors, setValidationErrors] = useState({});

  // Get alert data with null checks
  const alert = formState?.alert || {};
  const meta = alert.meta || {};
  
  // Check for process-initiated flags in both root level and meta
  const isProcessInitiated = 
    meta.initiatedFromProcess === true || 
    alert.isProcessInitiated === true;
    
  // Check for lock flags in both places
  const lockProcessName = 
    meta.lockProcessName === true || 
    (isProcessInitiated && meta.targetProcessName);
    
  // Get target process name from either location
  const targetProcessName = 
    meta.targetProcessName || 
    alert.targetProcessName || 
    '';
    

  
  /**
   * Update a specific rule in the alert
   * 
   * @param {number} index - Index of the rule to update
   * @param {Object} updates - Updates to apply to the rule
   */
  const updateRule = (index, updates) => {
    const updatedRules = [...formState.alert.rules];
    updatedRules[index] = { ...updatedRules[index], ...updates };
    
    updateFormState({
      alert: {
        ...formState.alert,
        rules: updatedRules
      }
    });
    
    // Clear validation error for this rule if it exists
    if (validationErrors[`rule-${index}`]) {
      const updatedErrors = { ...validationErrors };
      delete updatedErrors[`rule-${index}`];
      setValidationErrors(updatedErrors);
    }
  };
  
  /**
   * Validate a rule value based on its condition
   * 
   * @param {string} value - The value to validate
   * @param {string} condition - The condition to validate against
   * @param {string} event - The event name
   * @returns {boolean} Whether the value is valid
   */
  const validateRuleValue = (value, condition, event) => {
    // For numeric conditions, value must be a number
    if ([
      AlertCondition.GREATER_THAN,
      AlertCondition.LESS_THAN,
      AlertCondition.GREATER_THAN_OR_EQUALS,
      AlertCondition.LESS_THAN_OR_EQUALS
    ].includes(condition)) {
      return !isNaN(parseFloat(value)) && isFinite(value);
    }
    
    // For equals and not equals, value can be anything but not empty
    if ([AlertCondition.EQUALS, AlertCondition.NOT_EQUALS].includes(condition)) {
      return value !== undefined && value !== null && value !== '';
    }
    
    // For contains, value must be a string and not empty
    if (condition === AlertCondition.CONTAINS) {
      return typeof value === 'string' && value.trim() !== '';
    }
    
    return true;
  };
  
  /**
   * Get the appropriate input type for a rule based on its event and condition
   * 
   * @param {string} event - The event name
   * @param {string} condition - The condition
   * @returns {string} The input type
   */
  const getInputTypeForRule = (event, condition) => {
    // For connection events, use boolean select
    if (
      event === 'Websocket.Connected' || 
      event === 'Websocket.Disconnected' || 
      event === 'Server.Connected' || 
      event === 'Server.Disconnected'
    ) {
      return 'boolean-select';
    }
    
    // For percentage events, use percent-select
    if (event.includes('percent')) {
      return 'percent-select';
    }
    
    // For other numeric events, use number input
    if (
      event.includes('usage') || 
      event.includes('available') || 
      event.includes('free') ||
      event.includes('bytes') ||
      event.includes('packets')
    ) {
      return 'number';
    }
    
    // For log level, use select input
    if (event === 'level') {
      return 'select';
    }
    
    // For everything else, use text input
    return 'text';
  };
  
  /**
   * Get available options for a select input based on event
   * 
   * @param {string} event - The event name
   * @returns {string[]} Available options
   */
  const getOptionsForEvent = (event) => {
    if (event === 'level') {
      return ['INFO', 'WARNING', 'ERROR', 'CRITICAL', 'DEBUG'];
    }
    
    // For connection events, return boolean options
    if (
      event === 'Websocket.Connected' || 
      event === 'Websocket.Disconnected' || 
      event === 'Server.Connected' || 
      event === 'Server.Disconnected'
    ) {
      return ['true', 'false'];
    }
    
    // For percentage events, return 1-100 options
    if (event.includes('percent')) {
      const percentOptions = [];
      for (let i = 1; i <= 100; i++) {
        percentOptions.push(i.toString());
      }
      return percentOptions;
    }
    
    return [];
  };
  
  /**
   * Render the appropriate input for a rule value
   * 
   * @param {Object} rule - The rule object
   * @param {number} index - Index of the rule
   * @returns {JSX.Element} The input element
   */
  const renderValueInput = (rule, index) => {
    const inputType = getInputTypeForRule(rule.event, rule.condition);
    
    // For connection events, force condition to 'equals'
    if (
      ['Websocket.Connected', 'Websocket.Disconnected', 'Server.Connected', 'Server.Disconnected'].includes(rule.event) &&
      rule.condition !== AlertCondition.EQUALS
    ) {
      // Auto-set condition to equals for these events
      setTimeout(() => {
        updateRule(index, { condition: AlertCondition.EQUALS });
      }, 0);
    }
    
    switch (inputType) {
      case 'number':
        // Add specific placeholders and step values for memory and disk metrics
        let placeholder = "Enter value";
        let step = "any";
        
        if (rule.event === 'memory_usage' || rule.event === 'memory.available') {
          placeholder = "Enter value in GB";
          step = "0.1";
        } else if (rule.event === 'disk_usage' || rule.event === 'disk.free') {
          placeholder = "Enter value in GB";
          step = "0.1";
        }
        
        return (
          <div className="rule-value-input-container">
            <input
              type="number"
              className="rule-value-input"
              value={rule.value || ''}
              onChange={(e) => updateRule(index, { value: e.target.value })}
              placeholder={placeholder}
              step={step}
            />
            {(rule.event === 'memory_usage' || rule.event === 'disk_usage' || 
              rule.event === 'memory.available' || rule.event === 'disk.free') && (
              <span className="rule-value-unit">GB</span>
            )}
          </div>
        );
      
      case 'percent-select':
        // For percentage events, provide a dropdown with values 1-100
        return (
          <select
            className="rule-value-select"
            value={rule.value || ''}
            onChange={(e) => updateRule(index, { value: e.target.value })}
          >
            <option value="">Select percentage (1-100)</option>
            {getOptionsForEvent(rule.event).map(option => (
              <option key={option} value={option}>{option}%</option>
            ))}
          </select>
        );
      
      case 'boolean-select':
        // For connection events, we want a boolean select with true/false options
        return (
          <select
            className="rule-value-select"
            value={rule.value || ''}
            onChange={(e) => updateRule(index, { value: e.target.value })}
          >
            <option value="">Select True/False</option>
            {getOptionsForEvent(rule.event).map(option => (
              <option key={option} value={option}>{option}</option>
            ))}
          </select>
        );
      
      case 'select':
        return (
          <select
            className="rule-value-select"
            value={rule.value || ''}
            onChange={(e) => updateRule(index, { value: e.target.value })}
          >
            <option value="">Select a value</option>
            {getOptionsForEvent(rule.event).map(option => (
              <option key={option} value={option}>{option}</option>
            ))}
          </select>
        );
      
      default:
        return (
          <input
            type="text"
            className="rule-value-input"
            value={rule.value || ''}
            onChange={(e) => updateRule(index, { value: e.target.value })}
            placeholder="Enter value"
          />
        );
    }
  };
  
  /**
   * Validate all rules and return whether they are all valid
   * 
   * @returns {boolean} Whether all rules are valid
   */
  const validateAllRules = () => {
    const errors = {};
    let isValid = true;
    
    formState.alert.rules.forEach((rule, index) => {
      if (!validateRuleValue(rule.value, rule.condition, rule.event)) {
        errors[`rule-${index}`] = 'Please enter a valid value for this rule';
        isValid = false;
      }
      
      // Validate process name pattern if targeting is enabled
      if (formState.alert.type === AlertType.PROCESS &&
          rule.processNameTargetingEnabled &&
          (!rule.processNamePattern || rule.processNamePattern.trim() === '')) {
        errors[`process-pattern-${index}`] = 'Process Name/Pattern cannot be empty when targeting is enabled';
        isValid = false;
      }
    });
    
    setValidationErrors(errors);
    return isValid;
  };
  

  // For process-initiated alerts, ensure process name targeting is enabled and filled
  useEffect(() => {
    // Only run for Process alert type with rules and if process-initiated
    if (isProcessInitiated && formState.alert.type === AlertType.PROCESS && formState.alert.rules.length > 0) {
      // Check if we need to update rules to enable process targeting
      const updatedRules = formState.alert.rules.map(rule => {
        if (!rule.processNameTargetingEnabled || rule.processNamePattern !== targetProcessName) {
          return {
            ...rule,
            processNameTargetingEnabled: true,
            processNamePattern: targetProcessName
          };
        }
        return rule;
      });
      
      // Only update if changes were made
      if (JSON.stringify(updatedRules) !== JSON.stringify(formState.alert.rules)) {
        updateFormState({
          alert: {
            ...formState.alert,
            rules: updatedRules
          }
        });
      }
    }
  }, [isProcessInitiated, targetProcessName, formState.alert.type, formState.alert.rules, updateFormState]);
  
  // Validate rules when component mounts or rules change
  useEffect(() => {
    validateAllRules();
  }, [formState.alert.rules]);
  
  return (
    <div className="rule-builder-container">
      <h3 className="step-title">Configure Alert Rules</h3>
      <p className="step-description">
        Set conditions and values for each event you selected. These rules determine when the alert will be triggered.
      </p>
      
      {formState.alert.rules.length === 0 ? (
        <div className="no-rules-message">
          <i className="fa fa-exclamation-triangle"></i>
          No events selected. Please go back and select at least one event.
        </div>
      ) : (
        <div className="rules-list">
          {formState.alert.rules.map((rule, index) => {
            // Check if this is a connection event
            const isConnectionEvent = [
              'Websocket.Connected', 'Websocket.Disconnected', 
              'Server.Connected', 'Server.Disconnected'
            ].includes(rule.event);
            
            return (
              <div key={index} className="rule-item">
                <div className="rule-event">
                  <span className="rule-label">Event:</span>
                  <span className="rule-event-name">{rule.event}</span>
                </div>
                
                <div className="rule-condition">
                  <span className="rule-label">Condition:</span>
                  <select
                    className="rule-condition-select"
                    value={rule.condition}
                    onChange={(e) => updateRule(index, { condition: e.target.value })}
                    disabled={isConnectionEvent} // Disable for connection events
                    title={isConnectionEvent ? 'Connection events only support equals condition' : ''}
                  >
                    {Object.values(AlertCondition).map(condition => (
                      <option 
                        key={condition} 
                        value={condition}
                        disabled={isConnectionEvent && condition !== AlertCondition.EQUALS}
                      >
                        {getConditionDisplayText(condition)}
                      </option>
                    ))}
                  </select>
                </div>
                
                <div className="rule-value">
                  <span className="rule-label">Value:</span>
                  {renderValueInput(rule, index)}
                  {validationErrors[`rule-${index}`] && (
                    <div className="rule-error">{validationErrors[`rule-${index}`]}</div>
                  )}
                </div>
                
                {/* Process Name Targeting UI - only show for Process alert type */}
                {formState.alert.type === AlertType.PROCESS && (
                  <div className="process-targeting-container">
                    <div className="process-targeting-checkbox">
                      <QWebToggle
                        checked={rule.processNameTargetingEnabled || false}
                        onChange={(e) => updateRule(index, { processNameTargetingEnabled: e.target.checked })}
                        labelOn={isProcessInitiated 
                          ? `Target process: ${targetProcessName}` 
                          : 'Target specific process(es)'}
                        labelOff={isProcessInitiated 
                          ? `Target process: ${targetProcessName}` 
                          : 'Target specific process(es)'}
                        disabled={lockProcessName}
                        aria-label="Enable process targeting"
                        className="process-targeting-toggle"
                      />
                      {lockProcessName && (
                        <span style={{ marginLeft: '5px', color: '#0077cc' }}>
                          <i className="fa fa-lock" style={{ fontSize: '12px' }}></i>
                        </span>
                      )}
                    </div>
                    
                    {rule.processNameTargetingEnabled && (
                      <div className="process-targeting-pattern">
                        <label htmlFor={`process-pattern-${index}`}>
                          {isProcessInitiated ? 'Process Name:' : 'Process Name/Pattern:'}
                        </label>
                        <input
                          type="text"
                          id={`process-pattern-${index}`}
                          className={`${validationErrors[`process-pattern-${index}`] ? 'has-error' : ''} ${lockProcessName ? 'process-name-input-locked' : ''}`}
                          value={rule.processNamePattern || ''}
                          onChange={(e) => updateRule(index, { processNamePattern: e.target.value })}
                          placeholder="e.g., nginx, nginx*, *server*, *worker*"
                          readOnly={lockProcessName}
                        />
                        {validationErrors[`process-pattern-${index}`] && (
                          <div className="rule-error">{validationErrors[`process-pattern-${index}`]}</div>
                        )}
                        <div className="process-pattern-help">
                          {isProcessInitiated ? (
                            <small>
                              <i className="fa fa-info-circle" style={{ marginRight: '4px' }}></i>
                              This process name is locked because the alert was initiated from the process panel.
                            </small>
                          ) : (
                            <small>
                              Wildcards (*) are supported. Examples: <code>nginx</code> (exact match), 
                              <code>nginx*</code> (starts with), <code>*worker</code> (ends with), 
                              <code>*nginx*</code> (contains).
                            </small>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
      
      <div className="rule-builder-help">
        <h4>How Rules Work</h4>
        <p>
          When multiple rules are defined for an alert, all rules must be satisfied for the alert to trigger.
          Each rule consists of an event, a condition, and a value to compare against.
        </p>
        <p>
          For example, if you set a rule for "cpu.percent" with condition "is greater than" and value "90",
          the alert will trigger when CPU usage exceeds 90%.
        </p>
        <p>
          <strong>Note:</strong> For memory and disk metrics (memory_usage, disk_usage, memory.available, disk.free), values are in gigabytes (GB). For example, if you set a rule for
          "memory_usage" with condition "is greater than" and value "2", the alert will trigger when memory usage exceeds 2 GB.
        </p>
      </div>
    </div>
  );
}

export default RuleBuilder;
